import { api_fetch_throws } from "../../../api/client"

/** What the catalog and NetSuite say about one SKU row of a forecast version. */
export interface SkuOverlay {
  stable_key: string
  product_id: string
  netsuite_item_id?: string | null
  /** The NetSuite item name, not the name the row was imported under. */
  name: string
  units_per_case?: number | null
  packs_per_case?: number | null
}

export type CustomerSource = "netsuite" | "custom"

/** Where one forecast customer row comes from. */
export interface CustomerOverlay {
  stable_key: string
  name: string
  source: CustomerSource
  netsuite_id?: string | null
  netsuite_name?: string | null
}

export interface CatalogOverlay {
  skus: SkuOverlay[]
  customers: CustomerOverlay[]
}

export async function getCatalogOverlay(version_id: string): Promise<CatalogOverlay> {
  return api_fetch_throws<CatalogOverlay>(`/forecast-versions/${version_id}/catalog-overlay`)
}

export interface DirectoryProduct {
  id: string
  name: string
  netsuite_item_id?: string | null
  item_type?: string | null
  quantity_on_hand?: string | null
  units_per_case?: number | null
  packs_per_case?: number | null
  product_line?: string | null
  /** The product this one was folded into, when it is a duplicate. */
  superseded_by?: string | null
}

export interface DirectoryCustomer {
  name: string
  source: CustomerSource
  netsuite_id?: string | null
  customer_type?: string | null
  /** Forecast rows that land on this customer, across plan versions. */
  forecast_rows: string[]
}

export interface ForecastDirectory {
  products: DirectoryProduct[]
  customers: DirectoryCustomer[]
}

export async function getForecastDirectory(): Promise<ForecastDirectory> {
  return api_fetch_throws<ForecastDirectory>("/forecast-mappings/directory")
}

export interface TradeSpendRow {
  customer: string
  /** The account billed, when it is not the customer itself (a distributor). */
  bill_to?: string | null
  /** `YYYY-MM` → dollars. */
  by_month: Record<string, number>
  total: number
  promo_count: number
}

export interface ForecastTradeSpend {
  months: string[]
  rows: TradeSpendRow[]
  total_by_month: Record<string, number>
  total: number
  /** Promos in the year the calendar holds no amount for. */
  promos_without_amount: number
  /** Pending promos left out, and what they would add. */
  pending_promos: number
  pending_amount: number
}

export async function getForecastTradeSpend(version_id: string): Promise<ForecastTradeSpend> {
  return api_fetch_throws<ForecastTradeSpend>(`/forecast-versions/${version_id}/trade-spend`)
}
